import React, { useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import TicketList from './components/TicketList'
import TicketChat from './components/TicketChat'
import CreateTicketModal from './components/CreateTicketModal'
import { addTicketComment, closeTicket, createTicket, fetchDepartments, getTickets } from './api'
import { BASE_QUERY_CONFIG } from '../../utils/constants'
import { Toast } from 'primereact/toast'
import { onQueryError, onQuerySuccess } from '../../components/api'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { getImage } from '../../utils/image'
import Store from 'store'
import { OverlayPanel } from 'primereact/overlaypanel'

export enum UserTicketStatus {
  New = 'New',
  Evaluation = 'Evaluation',
  Estimation = 'Estimation',
  Planned = 'Planned',
  InProgress = 'In Progress',
  Testing = 'Testing',
  Completed = 'Completed',
  Closed = 'Closed',
}




// Define which statuses are considered open/closed
const OPEN_STATUSES = ['New', 'In Progress', 'Pending']
const CLOSED_STATUSES = ['Closed', 'Resolved', 'Rejected', 'Completed']

const SupportTickets = () => {
  const navigate = useNavigate()
  const toast = useRef<Toast>(null)
  const op = useRef<any>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [selectedDept, setSelectedDept] = useState<any>(null)
  const [title, setTitle] = useState('')
  const [ticketStatus, setTicketStatus] = useState('open')
  const [selectedTicket, setSelectedTicket] = useState<any>(null)
  const [newComment, setNewComment] = useState('')

  const { data: departmentsData } = useQuery('departments', fetchDepartments, BASE_QUERY_CONFIG)
  const departments = (departmentsData || []).map((d: any) => ({ label: d.name, value: d }))

  const {
    data: ticketsData,
    refetch: refetchTickets,
    isFetching: ticketsFetching,
  } = useQuery('tickets', getTickets, BASE_QUERY_CONFIG)

  const tickets = Array.isArray(ticketsData) ? ticketsData : ticketsData?.data || []
  // Filter tickets based on selected tab
  const filteredTickets = tickets.filter((t: any) =>
    ticketStatus === 'open' ? OPEN_STATUSES.includes(t.status) : CLOSED_STATUSES.includes(t.status),
  )

  React.useEffect(() => {
    if (filteredTickets.length) {
      // If no ticket is selected, or if the selected ticket is not in the current filtered list
      const isSelectedTicketInCurrentList = selectedTicket && filteredTickets.find((t: any) => t.id === selectedTicket.id)
      if (!selectedTicket || !isSelectedTicketInCurrentList) {
        setSelectedTicket(filteredTickets[0])
      }
    } else {
      // If no tickets in the current tab, clear selection
      setSelectedTicket(null)
    }
  }, [filteredTickets, selectedTicket, ticketStatus])

  const [mutate] = useMutation<any, any, any>(createTicket, {
    onSuccess: () => {
      onQuerySuccess(toast, 'Ticket created successfully')
      setShowCreate(false)
      setSelectedDept(null)
      setTitle('')
      refetchTickets()
    },
    onError: () => {
      onQueryError(toast, 'Failed to create ticket')
    },
  })

  const [closeMutate] = useMutation(closeTicket, {
    onSuccess: () => {
      onQuerySuccess(toast, 'Ticket closed successfully')

      setSelectedTicket(null)

      refetchTickets()
    },
    onError: () => {
      onQueryError(toast, 'Failed to close ticket')
    },
  })

  // Helper to update selectedTicket to the latest object from tickets array
  const updateSelectedTicket = (ticketsArr: any[], ticketId: any) => {
    if (!ticketId) return
    const updated = ticketsArr.find((t: any) => t.id === ticketId)
    if (updated) setSelectedTicket(updated)
  }

  // Wrap refetchTickets to update selectedTicket after refresh
  const handleRefetchTickets = async () => {
    const result = await refetchTickets()
    const newTickets = Array.isArray(result.data) ? result.data : result.data?.data || []
    updateSelectedTicket(newTickets, selectedTicket?.id)
  }

  const [addCommentMutate, { isLoading: isSendingComment }] = useMutation(addTicketComment, {
    onSuccess: async () => {
      onQuerySuccess(toast, 'Comment added successfully')
      setNewComment('')
      const result = await refetchTickets()
      const newTickets = Array.isArray(result.data) ? result.data : result.data?.data || []
      updateSelectedTicket(newTickets, selectedTicket?.id)
    },
    onError: (error) => {
      console.error('Error adding comment:', error)
      onQueryError(toast, 'Failed to add comment')
    },
  })

  const handleSendComment = (attachments?: File[]) => {
    if ((!newComment && (!attachments || attachments.length === 0)) || !selectedTicket?.id) return
    
    const formData = new FormData()
    formData.append('text', newComment || '')
    formData.append('ticketId', selectedTicket.id)
    formData.append('affiliateUserId', Store.get('aff-id'))
    
    if (attachments && attachments.length > 0) {
      attachments.forEach((file, index) => {
        formData.append(`attachments`, file)
      })
    }
    
    addCommentMutate(formData)
  }

  const handleCloseTicket = () => {
    if (selectedTicket?.id) {
      closeMutate(selectedTicket.id)
    }
  }

  const handleSubmitTicket = () => {
    if (!title || !selectedDept) {
      onQueryError(toast, 'Please fill all fields')
      return
    }

    mutate({
      title,
      departmentId: (selectedDept as any).id,
    })
  }

  const logoutFunc = () => {
    // logout
    Store.clearAll()
    navigate('/leads')
    window.location.reload()
  }

  return (
    <div style={{ padding: 32, height: '100vh', background: '#f5f6fa', overflow: 'hidden' }}>
      <Toast ref={toast} />
      {/* Header/topbar */}
      <div
        className="topbar p-grid p-justify-between p-align-center p-shadow-4"
        style={{
          marginBottom: 24,
          width: '100vw',
          minHeight: 64,
          padding: '0',
          boxSizing: 'border-box',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1000,
          background: '#fff',
          boxShadow: '0 1px 4px #0001',
        }}
      >
        <div className="brand__options p-grid  p-align-center p-ml-3">
          <img
            src={getImage('brand-logo.jpg')}
            alt="brand logo"
            style={{ height: 40, marginRight: 12 }}
          />
          <span
            className="brand__name"
            style={{ fontWeight: 700, fontSize: 20, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            Affiliate Report
          </span>
        </div>
        <div
          className="p-grid p-align-center p-mr-3  language__options"
          style={{ marginTop: 0, marginLeft: 0 }}
        >
          {Store.get('aff-name') || 'Anonymous'}
          <OverlayPanel className="ui-overlaypanel" ref={op} dismissable>
            <span
              style={{ display: 'block', cursor: 'pointer' }}
              onClick={logoutFunc}
            >
              Logout
            </span>
          </OverlayPanel>
          <i className="pi pi-chevron-down p-ml-3" onClick={(e) => op.current.toggle(e)}></i>
        </div>
      </div>
      <div
        style={{
          maxWidth: '80dvw',
          margin: '0 auto',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 8px #0001',
          padding: 24,
          marginTop: 40, // add margin to prevent overlap with fixed header
          display: 'flex',
          flexDirection: 'row',
          height: 'calc(100vh - 100px)', // adjust for header and padding
          minHeight: 0,
        }}
      >
        <div style={{ display: 'flex', gap: 24, flex: 1, minHeight: 0, position: 'relative' }}>
          {/* Spinner overlay when loading/fetching */}
          {ticketsFetching && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.5)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="4" />
            </div>
          )}
          {/* Left side: header/tabs + ticket list */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minWidth: 360,
              flex: '0 0 340px',
            }}
          >
            {/* Header/tabs area (everything above TicketList) */}
            <div style={{ flex: '0 0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                <h2 style={{ marginBottom: 0, marginRight: 16 }}>Support</h2>
                <Button
                  label="Back to Leads"
                  icon="pi pi-arrow-left"
                  className="p-button-text"
                  onClick={() => navigate('/leads')}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #eee',
                  marginBottom: 16,
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: 18,
                    marginRight: 16,
                    borderBottom: '2px solid #1976d2',
                    paddingBottom: 8,
                  }}
                >
                  Tickets
                </span>
                <Button
                  icon="pi pi-refresh"
                  className="p-button-text p-ml-2"
                  onClick={handleRefetchTickets}
                  tooltip="Refresh tickets"
                  aria-label="Refresh tickets"
                  style={{ marginLeft: 8 }}
                  disabled={ticketsFetching}
                />
              </div>
            </div>
            {/* TicketList area */}
            <div style={{ flex: '1 1 0%', minHeight: 0, overflowY: 'auto' }}>
              <TicketList
                tickets={filteredTickets}
                selectedTicket={selectedTicket}
                onSelectTicket={setSelectedTicket}
                ticketStatus={ticketStatus}
                setTicketStatus={setTicketStatus}
                onCreateTicket={() => setShowCreate(true)}
              />
            </div>
          </div>
          {/* Right side: chat */}
          <TicketChat
            filteredTickets={filteredTickets}
            ticket={selectedTicket}
            newComment={newComment}
            setNewComment={setNewComment}
            onSendComment={handleSendComment}
            onCloseTicket={handleCloseTicket}
            onRefreshComments={handleRefetchTickets}
            isLoading={isSendingComment}
          />
        </div>
      </div>
      <CreateTicketModal
        visible={showCreate}
        onHide={() => setShowCreate(false)}
        departments={departments}
        selectedDept={selectedDept}
        setSelectedDept={setSelectedDept}
        title={title}
        setTitle={setTitle}
        onSubmit={handleSubmitTicket}
      />
    </div>
  )
}

export default SupportTickets
